<template>
  <font-awesome-icon v-bind="$attrs" :icon="[useLib, icon]" />
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

/**

##Icon wrapper component.  
This component is used to incapsulate icons all over the projects and unify icons usage. For now font awesome is used.


###Font awesome
This component only provides generic component with properties. It does not include any icons and it does not load any icons to target project.
Icons library (regular, solid, light, duotone, brand) should be installed in the target project and specific icon should be loaded.



###How to use in project
1. Add auth token
    - Add `.npmrc` file to the project root (if it is not added yet)
    - In the `.npmrc` file add
    
    ```
@fortawesome:registry=https://npm.fontawesome.com/
//npm.fontawesome.com/:_authToken=<TOKEN>
    ```

1. Install vue font awesome package  
    `npm i --save @fortawesome/vue-fontawesome@latest`

1. Install font awesome core  
    `npm install --save @fortawesome/fontawesome-svg-core`

1. Install font awesome pro  
    `npm install --save @fortawesome/fontawesome-pro`

1. Install style library - it is prefered to install pro library
    - `npm i --save @fortawesome/pro-light-svg-icons` \- light icons style is used by default all over the projects, so this library is required to install.
    - `npm i --save @fortawesome/pro-regular-svg-icons` \- regular icons library, only install it if you really need icons of this style.
    - `npm i --save @fortawesome/pro-solid-svg-icons` \- solid icons library, only install it if you really need icons of this style.
    - `npm i --save @fortawesome/pro-duotone-svg-icons` \- duotone icons library, only install it if you really need icons of this style.
    - `npm i --save @fortawesome/free-brands-svg-icons` \- brands icons library, only install it if you really need any brand icon. All brand icons are free, so there is no pro lib for brands.

1. Globally load icon in `main.js`
    - `import { library } from '@fortawesome/fontawesome-svg-core';` \- import library manager
    - `import { faCoffee, faUserSecret } from '@fortawesome/pro-light-svg-icons';` \- import only icons that you need
    - `library.add(faCoffee, faUserSecret);` \- register icons with library manager
    - In the production bundle only needed icons will be added. Other icons will be excluded by tree-shaking.

1. You can add icon locally in component the same way as in step above.

1. Use icon
    - `<base-icon icon="user-secret" size="10x"/>`

1. Base components use these icons, so if you want to use base components you need to include these icons in target project. You can include all these icons, or if you use only some of base components, you can include specific icons. List of icons dependencies is on every base component docs page.

```js
import { library } from '@fortawesome/fontawesome-svg-core';

import {
  faCalendarAlt,
  faArrowToRight,
  faChevronRight,
  faChevronDoubleRight,
  faArrowToLeft,
  faChevronLeft,
  faChevronDoubleLeft,
  faTimes,
  faTrashAlt,
  faQuestionCircle,
  faCaretRight,
  faCaretDown,
  faFolder,
  faPlus,
  faDownload,
  faClock,
  faGripHorizontal,
  faPencil,
} from '@fortawesome/pro-light-svg-icons';

import {
  faCircle as fasCircle,
} from '@fortawesome/pro-solid-svg-icons';

library.add(
  //light
  faCalendarAlt,
  faArrowToRight,
  faChevronRight,
  faChevronDoubleRight,
  faArrowToLeft,
  faChevronLeft,
  faChevronDoubleLeft,
  faTimes,
  faTrashAlt,
  faQuestionCircle,
  faCaretRight,
  faCaretDown,
  faFolder,
  faPlus,
  faDownload,
  faClock,
  faGripHorizontal,
  faPencil,
  //solid
  fasCircle,
);
```


 */
export default {
  components: { FontAwesomeIcon },
  props: {
    /**
     * Font Awesome icon name: coffee, user-secret...
     */
    icon: {
      type: String,
      required: true,
    },
    /**
     * Use regular style library
     */
    regular: {
      type: Boolean,
      default: false,
    },
    /**
     * Use solid style library
     */
    solid: {
      type: Boolean,
      default: false,
    },
    /**
     * Use duotone style library
     */
    duotone: {
      type: Boolean,
      default: false,
    },
    /**
     * Use brand style library
     */
    brand: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    useLib() {
      return this.regular
        ? "far"
        : this.solid
        ? "fas"
        : this.duotone
        ? "fad"
        : this.brand
        ? "fab"
        : "fal";
    },
  },
};
</script>